import { useEffect, useRef } from 'react'

// #294 redesign (CP 294 Section 1.4) — while critical alerts are open, the browser tab's icon carries a red
// count badge: the page's own icon (/detercam.png) drawn on a 64x64 canvas with a red circle top right and
// the count in white (up to 9, then "9+"). When none is open, or the layout unmounts (logout), the link gets
// its exact original href back. Best effort: no icon link, no canvas, an image that does not load or a
// canvas the browser will not export leave the icon as it is; some browsers (Safari) ignore a changed icon.

const SIZE = 64
const RADIUS = 18
const BADGE_RED = '#dc2626'
const WHITE = '#ffffff'

interface Saved { link: HTMLLinkElement; href: string | null }

function drawBadge(img: HTMLImageElement, count: number): string | null {
  const canvas = document.createElement('canvas')
  canvas.width = SIZE
  canvas.height = SIZE
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.drawImage(img, 0, 0, SIZE, SIZE)
  const label = count > 9 ? '9+' : String(count)
  const cx = SIZE - RADIUS - 2
  const cy = RADIUS + 2
  ctx.beginPath()
  ctx.arc(cx, cy, RADIUS, 0, 2 * Math.PI)
  ctx.fillStyle = BADGE_RED
  ctx.fill()
  ctx.lineWidth = 3
  ctx.strokeStyle = WHITE
  ctx.stroke()
  ctx.fillStyle = WHITE
  ctx.font = `bold ${label.length > 1 ? 22 : 28}px system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(label, cx, cy + 1)
  return canvas.toDataURL('image/png')
}

function restore(saved: { current: Saved | null }) {
  const s = saved.current
  if (!s) return
  if (s.href === null) s.link.removeAttribute('href')
  else s.link.setAttribute('href', s.href)
  saved.current = null
}

/** Show `count` on the tab icon while it is above 0 (see the file comment). */
export function useCriticalTabBadge(count: number): void {
  const saved = useRef<Saved | null>(null)

  useEffect(() => {
    if (count <= 0) {
      restore(saved)
      return
    }
    if (typeof document === 'undefined') return
    const link = saved.current?.link ?? document.querySelector<HTMLLinkElement>('link[rel~="icon"]')
    if (!link) return
    if (!saved.current) saved.current = { link, href: link.getAttribute('href') }
    const source = saved.current.href
    if (!source) return
    let cancelled = false
    try {
      const img = new Image()
      img.onload = () => {
        if (cancelled) return
        try {
          const url = drawBadge(img, count)
          if (url && !cancelled) link.setAttribute('href', url)
        } catch {
          // a canvas the browser will not export (or no 2D support): the icon stays as it is
        }
      }
      img.onerror = () => {}
      // Always from the original icon, never from a badge drawn earlier.
      img.src = source
    } catch {
      // no Image in this environment
    }
    return () => { cancelled = true }
  }, [count])

  useEffect(() => () => restore(saved), [])
}
